/** Route guard. Redirects unauthenticated users to /auth/login, carrying the
 *  interrupted URL (path + filters) as `?returnTo=` so it survives full page
 *  loads — router state alone does not. */
import { Alert, Box } from '@mui/material';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from './AuthProvider';
import { locationReturnTo } from './returnTo';

interface Props {
  children: React.ReactNode;
  requiredPerm?: string;
}

export function RequireAuth({ children, requiredPerm }: Props) {
  const auth = useAuth();
  const location = useLocation();

  if (!auth.user) {
    return (
      <Navigate
        to={`/auth/login?returnTo=${encodeURIComponent(locationReturnTo(location))}`}
        state={{ from: location }}
        replace
      />
    );
  }

  if (requiredPerm && !auth.hasPerm(requiredPerm)) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">
          You don't have permission to view this page (<code>{requiredPerm}</code>).
        </Alert>
      </Box>
    );
  }

  return <>{children}</>;
}
